import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Vibration Meter / Seismograph — DeviceMotion accelerometer rendered
// onto an HTML5 Canvas at ~60 FPS. Three traces (X red, Y green, Z blue)
// + a magnitude trace. Peak-hold for the trailing N seconds.
//
// On iOS Safari: DeviceMotionEvent.requestPermission() must be called
// from a user gesture (the Start button below).

const TRACE_COLORS = {
  x: "#E0584E",
  y: "#5BD99A",
  z: "#5B9AD9",
  mag: "#F5A299",
};
const PEAK_WINDOW_MS = 3000;
const BUFFER_SECONDS = 10; // visible window
const SAMPLE_RATE_HZ = 60; // approximate

export default function VibrationMeter() {
  const [state, setState] = useState("idle");
  const [showTraces, setShowTraces] = useState({ x: true, y: true, z: true, mag: true });
  const [peak, setPeak] = useState(0);
  const canvasRef = useRef(null);
  const buf = useRef([]); // {t, x, y, z}
  const handler = useRef(null);
  const rafRef = useRef(null);
  const peakRef = useRef({ value: 0, until: 0 });

  const enable = async () => {
    if (typeof DeviceMotionEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceMotionEvent.requestPermission === "function") {
        const r = await DeviceMotionEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      handler.current = (e) => {
        const a = e.accelerationIncludingGravity || e.acceleration;
        if (!a) return;
        const t = performance.now();
        // Strip gravity ~9.81 from Z when using accelerationIncludingGravity
        // (rough — we don't know phone orientation; subtract magnitude of
        // gravity from the magnitude). For visualization purposes this is
        // sufficient.
        const x = a.x || 0, y = a.y || 0, z = a.z || 0;
        buf.current.push({ t, x, y, z });
        // Drop samples older than BUFFER_SECONDS
        const cutoff = t - BUFFER_SECONDS * 1000;
        while (buf.current.length && buf.current[0].t < cutoff) buf.current.shift();
        // Peak tracking on magnitude minus gravity
        const mag = Math.sqrt(x * x + y * y + z * z);
        const linMag = Math.abs(mag - 9.81);
        if (linMag > peakRef.current.value || t > peakRef.current.until) {
          peakRef.current = { value: linMag, until: t + PEAK_WINDOW_MS };
          setPeak(linMag);
        }
      };
      window.addEventListener("devicemotion", handler.current);
      setState("ok");
      requestAnimationFrame(draw);
    } catch {
      setState("denied");
    }
  };

  const draw = () => {
    const cv = canvasRef.current;
    if (!cv) { rafRef.current = requestAnimationFrame(draw); return; }
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth * dpr;
    const h = cv.clientHeight * dpr;
    if (cv.width !== w) cv.width = w;
    if (cv.height !== h) cv.height = h;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, w, h);

    // Background grid
    ctx.strokeStyle = "#1F2030";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(0, (i * h) / 4); ctx.lineTo(w, (i * h) / 4);
      ctx.stroke();
    }
    // Center line
    ctx.strokeStyle = "#2A2C3D";
    ctx.beginPath();
    ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2);
    ctx.stroke();

    const samples = buf.current;
    if (samples.length < 2) {
      rafRef.current = requestAnimationFrame(draw);
      return;
    }
    const tNow = performance.now();
    const tMin = tNow - BUFFER_SECONDS * 1000;
    // y-scale: clamp to ±20 m/s² (most vibration is well below this)
    const scale = h / 2 / 20;

    const drawTrace = (key, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5 * dpr;
      ctx.beginPath();
      let first = true;
      for (let i = 0; i < samples.length; i++) {
        const s = samples[i];
        const px = ((s.t - tMin) / (BUFFER_SECONDS * 1000)) * w;
        let v;
        if (key === "mag") {
          const m = Math.sqrt(s.x * s.x + s.y * s.y + s.z * s.z);
          v = m - 9.81; // strip gravity for magnitude
        } else {
          v = s[key];
        }
        const py = h / 2 - v * scale;
        if (first) { ctx.moveTo(px, py); first = false; } else ctx.lineTo(px, py);
      }
      ctx.stroke();
    };

    if (showTraces.x) drawTrace("x", TRACE_COLORS.x);
    if (showTraces.y) drawTrace("y", TRACE_COLORS.y);
    if (showTraces.z) drawTrace("z", TRACE_COLORS.z);
    if (showTraces.mag) drawTrace("mag", TRACE_COLORS.mag);

    rafRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (handler.current) window.removeEventListener("devicemotion", handler.current);
  }, []);

  const reset = () => {
    buf.current = [];
    peakRef.current = { value: 0, until: 0 };
    setPeak(0);
  };

  return (
    <ToolsShell title="Vibration Meter">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Visualize acceleration on three axes — useful for finding loose washing machines, vibrating tools, or a playful seismograph. Tap Start to enable motion sensors.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Motion sensor" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ position: "relative", width: "100%", height: 280, background: "#0F1018", borderRadius: 12, border: "1px solid #262839", overflow: "hidden" }}>
            <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
            <div style={{ position: "absolute", top: 8, right: 12, background: "rgba(15, 16, 24, 0.85)", padding: "4px 8px", borderRadius: 6, fontSize: 11, color: "#9A9DBE" }}>
              {BUFFER_SECONDS} s window · m/s²
            </div>
          </div>

          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(TRACE_COLORS).map(([key, color]) => (
              <button
                key={key}
                onClick={() => setShowTraces((s) => ({ ...s, [key]: !s[key] }))}
                style={{
                  background: showTraces[key] ? `${color}33` : "transparent",
                  border: `1px solid ${color}`,
                  color: showTraces[key] ? color : "#7C8099",
                  borderRadius: 6,
                  padding: "5px 10px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  textTransform: "uppercase",
                }}
              >
                {key}
              </button>
            ))}
            <button onClick={reset} style={{ marginLeft: "auto", background: "transparent", border: "1px solid #262839", color: "#9A9DBE", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}>
              Clear
            </button>
          </div>

          <div style={{ marginTop: 16, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Peak (last 3s)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
                {peak.toFixed(2)} m/s²
              </div>
            </div>
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Magnitude has gravity (9.81 m/s²) subtracted to show net vibration. Phone accelerometers are calibrated for tilt, not metrology — readings are approximate.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
